import { chatKindOf } from '@bt-common/schemas/wa/chat-kind';
import ReplyToMessageChip from '@/common/components/message-editor/reply-to-message-chip';
import { useAppSelector, useAppDispatch } from '@/common/reducers/hooks/store.hook';
import { makeSelectMessageDraftById, makeSelectNameForDisplay } from './redux/TeamInboxSelectors';
import { requestMessageMediaThunk } from './redux/TeamInboxThunks';
import AckIcon from './ack-Icon';
import { Box, SxProps, Theme, Typography } from '@mui/material';
import moment from 'moment';
import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import AddScheduledMessageLinkPreview from '@/extension/context/content/components/add-scheduled-message-button/add-scheduled-message-link-preview';
import WhatsAppPollPreview from '@common/components/message-editor/whatsapp-poll-preview';
import AddScheduledMessageAttachmentPreview from '@/extension/context/content/components/add-scheduled-message-button/add-schedulued-message-attachment-preview';
import MediaLoadingIndicator from '@/extension/context/content/components/add-scheduled-message-button/media-loading-indicator';
import { authorIdOf, getSenderColor, isMediaMessageType, getMessageText, getBackupThumbnailReason, reasonTextForMediaSkip } from './team-inbox-helpers';
import { selectUserObject } from '@/common/reducers/UserSelectors';
import type { UserInterface } from '@/common/models/user';
import LexicalMessageEditor from '@/common/components/message-editor/lexical/LexicalMessageEditor';
import MessageReactions from './message-reactions';
import QuotedMessageBlock from './quoted-message-block';
import VoiceMessagePlayer from './voice-message-player';
import { useMessageScroll } from './message-scroll-context';

interface TeamInboxMessageProps {
    message: any;
    /** First bubble of a same-sender cluster → gets the tail + squared top corner. */
    isFirstOfCluster?: boolean;
}

export default function TeamInboxMessage({ message, isFirstOfCluster = true }: TeamInboxMessageProps) {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const messageId = message?._id;
    const { highlightedMessageId } = useMessageScroll();
    const isHighlighted = !!highlightedMessageId && messageId === highlightedMessageId;

    // Use messageDraft for real-time preview updates (avoids race conditions with wrapper)
    const messageDraft = useAppSelector(state => makeSelectMessageDraftById(messageId)(state));

    // Derive display properties from message
    const fromMe = message?.fromMe ?? messageDraft?.waMessageKey?.fromMe !== false;
    const isGroupChat = chatKindOf(message?.chatId) === 'group';
    const authorId = authorIdOf(message);

    // Determine if we should fetch sender name
    const shouldSelectName = (
        message &&
        !message.fromMe &&
        message.type !== 'gp2' &&
        isGroupChat &&
        authorId
    );

    // Memoize the sender name selector
    const selectSenderName = useMemo(
        () => shouldSelectName
            ? makeSelectNameForDisplay(authorId!, message.senderName ?? message.notifyName)
            : () => '',
        [shouldSelectName, authorId, message?.senderName, message?.notifyName]
    );
    const senderName = useAppSelector(selectSenderName);

    // Infer hasMedia from message type
    const hasMedia = message?.hasAttachedMedia || message?.hasMedia || (message?.type ? isMediaMessageType(message.type) : false);

    const shouldFetchMedia = (
        message &&
        hasMedia &&
        (!message.mediaStatus || message.mediaStatus === 'notFetched') &&
        !message.isViewOnce
    );

    // Media loading state
    const mediaLoadingState = message?.mediaStatus ?? 'notFetched';

    // When a media message isn't showing the full-res backup, explain WHY.
    // Prefer the AUTHORITATIVE reason the backup engine recorded on the row
    // (persisted server-side, carried on the DB-tier message) — this is shown to
    // EVERY user so they understand why their media wasn't backed up. Fall back to
    // the config-based heuristic (a client GUESS) only under bt-debug. Suppressed
    // once full media is present (backed up / live WhatsApp) — nothing to explain.
    const showBackupDebug = typeof localStorage !== 'undefined' && localStorage.getItem('bt-debug') === 'true';
    const user = useAppSelector(selectUserObject) as UserInterface | undefined;
    const hasFullMedia =
        !!(message?.mediaData?.mediaUrl || message?.mediaData?.url) &&
        message?.mediaData?.isThumbnail !== true;
    const recordedSkipReason = hasFullMedia
        ? null
        : reasonTextForMediaSkip(message?.mediaSkipReason, message?.type);
    const backupThumbnailReason =
        recordedSkipReason ??
        ((showBackupDebug && message?.mediaData?.isThumbnail === true)
            ? getBackupThumbnailReason(message?.type, user?.backup?.media)
            : null);

    // Voice notes (ptt) and audio files get the WhatsApp-style player instead of
    // the generic attachment preview — but only once the media URL is resolved.
    // While loading, MediaLoadingIndicator still shows; on error the generic
    // preview remains the fallback (voiceUrl stays empty in both cases).
    const isVoiceMessage = message?.type === 'ptt' || message?.type === 'audio';
    const voiceUrl: string | undefined = message?.mediaData?.url || message?.mediaData?.mediaUrl;
    const showVoicePlayer = isVoiceMessage && !!voiceUrl && !message?.isViewOnce;

    // WhatsApp caps VISUAL media bubbles (~330px) instead of letting them
    // stretch to the 65% column like text does; the media itself is height-
    // capped and cover-cropped with soft corners.
    const isVisualMedia = ['image', 'video', 'sticker'].includes(message?.type) || !!message?.isGif;

    // Exotic / non-standard types (notification_template, interactive[_response],
    // list, newsletter_notification, e2e_notification, protocol,
    // biz_content_placeholder, unknown, …) that the generic attachment preview
    // would render as an ugly "Unsupported message type". For these we show the
    // human text from getMessageText (already returns body/caption or '') — never
    // the literal placeholder — or a subtle system chip / nothing when empty.
    const STANDARD_MESSAGE_TYPES = ['chat', 'image', 'video', 'sticker', 'ptt', 'audio',
        'document', 'vcard', 'multi_vcard', 'location', 'poll_creation'];
    const waSessionPhoneNumber = useAppSelector(state => state.TeamInboxReducer?.waSessionPhoneNumber ?? null);
    const isExoticType = !!message?.type && !STANDARD_MESSAGE_TYPES.includes(message.type) && !message?.isGif;
    // Reuse the shared mapping (no duplication) — displayNames not needed for
    // these non-participant types, so pass an empty map.
    const exoticText = isExoticType ? getMessageText(message, {}, waSessionPhoneNumber ?? '') : '';

    // Reactions presence — drives the extra bottom spacing so the overlapping
    // pill doesn't cover the following row.
    const hasReactions = Array.isArray(message?.reactions) && message.reactions.some((r: any) => (r?.text ?? '').trim());
    const isForwarded = !!message?.isForwarded;

    // Design variables based on message direction. WhatsApp: 7.5px radius, and a
    // squared top corner + tail ONLY on the first bubble of a same-sender cluster.
    const showTail = isFirstOfCluster;
    const designVars = {
        waMessageBg: fromMe ? 'var(--whatsapp-bubble-sent)' : 'var(--whatsapp-bubble-received)',
        bubbleBorderRadius: fromMe
            ? (showTail ? '7.5px 0 7.5px 7.5px' : '7.5px')
            : (showTail ? '0 7.5px 7.5px 7.5px' : '7.5px'),
    };

    // Message tail triangle style based on direction (only rendered on the first
    // bubble of a cluster).
    const triangleStyle: SxProps<Theme> = fromMe
        ? {
            position: 'absolute',
            top: '0px',
            right: '-7px',
            width: 0,
            height: 0,
            borderTop: '8px solid var(--whatsapp-bubble-sent)',
            borderRight: '8px solid transparent',
            borderLeft: '0px solid transparent',
            borderBottom: '0px solid transparent'
        }
        : {
            position: 'absolute',
            top: '0px',
            left: '-7px',
            width: 0,
            height: 0,
            borderTop: '8px solid var(--whatsapp-bubble-received)',
            borderRight: '0px solid transparent',
            borderLeft: '8px solid transparent',
            borderBottom: '0px solid transparent'
        };

    // Fetch media if needed
    useEffect(() => {
        if (shouldFetchMedia && messageId) {
            dispatch(requestMessageMediaThunk(messageId));
        }
    }, [shouldFetchMedia, dispatch, messageId]);

    // Should display ack icon only for outgoing messages
    const shouldDisplayAckIcon = fromMe;

    return (
        <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            width: 'fit-content',
            maxWidth: isVisualMedia ? '330px' : '100%',
            mb: hasReactions ? '10px' : 0,
        }}>
            {/* Message bubble with tail */}
            <Box sx={{
                position: 'relative',
                width: '100%',
                borderRadius: designVars.bubbleBorderRadius,
                // WhatsApp-style flash when this message is the jumped-to target of
                // a reply-quote click (soft accent glow, fades with the highlight).
                boxShadow: isHighlighted ? '0 0 0 4px var(--wa-flash, rgba(0,168,132,0.28))' : 'none',
                transition: 'box-shadow 1.2s ease',
            }}>
                <Box sx={{
                    bgcolor: designVars.waMessageBg,
                    borderRadius: designVars.bubbleBorderRadius,
                    padding: '6px 7px 8px 9px',
                    width: '100%',
                    boxSizing: 'border-box',
                    boxShadow: '0 1px 0.5px rgba(0,0,0,0.13)',
                    minWidth: 0
                }}>
                    {/* Forwarded label (WA: small italic gray with an arrow) */}
                    {isForwarded && (
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '3px',
                            mb: '2px',
                            color: 'var(--wa-meta-text, #667781)',
                            fontStyle: 'italic',
                            fontSize: '13px',
                        }}>
                            <span style={{ fontStyle: 'normal' }}>⤷</span>
                            <span>{t('teamInbox.forwardedLabel', 'Forwarded')}</span>
                        </Box>
                    )}

                    {/* Reply / quote block (WA: inside the bubble, above the body) */}
                    <QuotedMessageBlock message={message} />

                    {/* Sender Name (colored, above the first bubble of a cluster) */}
                    {senderName && isFirstOfCluster && (
                        <Box sx={{ mb: '2px', display: 'flex', alignItems: 'center' }}>
                            <Typography
                                variant="subtitle2"
                                sx={{
                                    fontWeight: 500,
                                    color: getSenderColor(senderName),
                                    fontSize: '12.8px',
                                    lineHeight: '17px',
                                    textOverflow: 'ellipsis',
                                    overflow: 'hidden',
                                    whiteSpace: 'nowrap',
                                    maxWidth: '100%'
                                }}
                            >
                                {senderName}
                            </Typography>
                        </Box>
                    )}
                    <Box dir="auto">
                        <ReplyToMessageChip height='83px' />

                        {/* Show link preview first */}
                        <AddScheduledMessageLinkPreview messageId={messageId} />

                        {/* Show media loading indicator */}
                        <MediaLoadingIndicator mediaLoadingState={mediaLoadingState} />

                        {/* Voice notes: WA-style player. Exotic/unsupported types:
                            human text (or a subtle system chip / nothing) instead
                            of the generic preview's "Unsupported message type".
                            Everything else keeps the generic attachment preview. */}
                        {isExoticType ? (
                            // Human text when the type carries any; otherwise render
                            // nothing (contentless system events like e2e_notification/
                            // protocol) — never the literal "Unsupported message type".
                            exoticText ? (
                                <Box sx={{
                                    fontSize: '14.2px',
                                    lineHeight: '19px',
                                    wordBreak: 'break-word',
                                    overflowWrap: 'break-word',
                                }} dir="auto">
                                    {exoticText}
                                </Box>
                            ) : null
                        ) : showVoicePlayer ? (
                            <VoiceMessagePlayer message={message} url={voiceUrl!} />
                        ) : (
                            <Box sx={isVisualMedia ? {
                                // WA media sizing: fill the (capped) bubble, cap the
                                // height, cover-crop, soft corners.
                                '& img, & video': {
                                    width: '100%',
                                    maxWidth: '100%',
                                    maxHeight: '340px',
                                    objectFit: 'cover',
                                    borderRadius: '6px',
                                    display: 'block',
                                },
                            } : undefined}>
                                <AddScheduledMessageAttachmentPreview messageId={messageId} isMediaClickable={true} />
                            </Box>
                        )}

                        {/* Why this media isn't the full backup — the engine-recorded
                            reason (shown to every user), e.g. its category is off in
                            Backup settings, over the size limit, or no longer on WA. */}
                        {backupThumbnailReason && (
                            <Box sx={{
                                mt: '4px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px',
                                fontSize: '11px',
                                lineHeight: '14px',
                                color: '#b45309',
                                backgroundColor: 'rgba(180,83,9,0.10)',
                                borderRadius: '4px',
                                padding: '3px 6px',
                            }}>
                                <span style={{ flexShrink: 0 }}>⚠️</span>
                                <span>{backupThumbnailReason}</span>
                            </Box>
                        )}
                        {messageDraft?.message && !messageDraft?.isPoll && !isExoticType && (
                            <>
                                {/* Show formatted message text */}
                                <Box
                                    sx={{
                                        fontSize: '14.2px',
                                        lineHeight: '19px',
                                        mt: messageDraft.linkPreview ? 1 : 0,
                                        wordBreak: 'break-word',
                                        overflowWrap: 'break-word',
                                        '& .lexical-editor': {
                                            padding: '0px !important',
                                            minHeight: 'auto !important'
                                        }
                                    }}
                                    dir='auto'
                                >
                                    <LexicalMessageEditor
                                        isPreview={true}
                                        content={messageDraft?.message}
                                        onFilesSelected={() => { }}
                                    />
                                </Box>
                            </>
                        )}

                        {/* Show poll if exists */}
                        {messageDraft?.isPoll && <WhatsAppPollPreview poll={messageDraft.poll} />}
                    </Box>

                    {/* Time and status */}
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        gap: 0.5,
                        mt: '2px',
                        color: 'var(--wa-meta-text, #667781)',
                        fontSize: '11px',
                        lineHeight: '15px',
                        flexShrink: 0
                    }}>
                        {messageDraft?.dueDate && moment(messageDraft.dueDate).format('h:mm A')}
                        {/* A rendered fromMe message has at least been sent.
                            AckIcon coalesces pending (0) / unknown (undefined) to
                            a single check, so it never shows the pending clock;
                            real delivered/read ticks still come via ack updates. */}
                        {shouldDisplayAckIcon && <AckIcon ackValue={messageDraft?.ack} />}
                    </Box>
                    {/* Debug: Show message key when bt-debug is enabled */}
                    {typeof localStorage !== 'undefined' && localStorage.getItem('bt-debug') === 'true' && messageId && (
                        <Box sx={{
                            fontSize: '0.65rem',
                            color: '#888',
                            mt: 0.25,
                            wordBreak: 'break-all',
                            textAlign: 'right',
                            fontFamily: 'monospace',
                            opacity: 0.7
                        }}>
                            {messageId}
                        </Box>
                    )}
                </Box>

                {/* Message tail triangle — only on the first bubble of a cluster */}
                {showTail && <Box sx={triangleStyle} />}

                {/* Reactions pill overlapping the bubble's bottom corner */}
                <MessageReactions reactions={message?.reactions} fromMe={fromMe} />
            </Box>
        </Box>
    );
}
